import React, { useState } from 'react';
import Select from 'react-select';
import DateFilter from './DateFilter';
import { useTranslation } from 'react-i18next';
import SaveSearchModal from './SaveSearchModal';
import { isEqual } from 'lodash';
import { API_BASE_URL } from '../config';
import { getFilterTranslations } from '../i18n/filters.js';
import { getAdminBadge } from '../utils/adminBadge';
import AiFacetsPanel from './AiFacetsPanel';
import ProductFilterField from './ProductFilterField';

const OBFUSCATABLE_COLUMNS = ['client', 'project', 'campaign'];

/** The look every select of the sidebar shares, light and dark. */
const SELECT_STYLES = {
  control: (base) => ({
    ...base,
    minHeight: '36px',
    height: 'auto',
    backgroundColor: 'var(--select-bg, #ffffff)',
    borderColor: 'var(--select-border, #d1d5db)',
    '&:hover': {
      borderColor: 'var(--select-border-hover, #9ca3af)'
    },
    '.dark &': {
      backgroundColor: 'var(--select-bg, #1e293b)',
      borderColor: 'var(--select-border, #475569)'
    }
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--select-bg, #ffffff)',
    border: '1px solid var(--select-border, #d1d5db)',
    zIndex: 999,
    '.dark &': {
      backgroundColor: 'var(--select-bg, #1e293b)',
      border: '1px solid var(--select-border, #475569)'
    }
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: 'var(--select-bg, #ffffff)',
    '.dark &': {
      backgroundColor: 'var(--select-bg, #1e293b)'
    }
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? 'var(--select-option-hover-bg, #f3f4f6)'
      : 'var(--select-bg, #ffffff)',
    color: 'var(--select-text, #111827)',
    '&:hover': {
      backgroundColor: 'var(--select-option-hover-bg, #f3f4f6)'
    },
    '.dark &': {
      backgroundColor: state.isFocused
        ? 'var(--select-option-hover-bg, #334155)'
        : 'var(--select-bg, #1e293b)',
      color: 'var(--select-text, #e2e8f0)',
      '&:hover': {
        backgroundColor: 'var(--select-option-hover-bg, #334155)'
      }
    }
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--select-text, #111827)',
    '.dark &': {
      color: 'var(--select-text, #e2e8f0)'
    }
  }),
  multiValue: (base) => ({
    ...base,
    margin: '2px',
    backgroundColor: 'var(--select-multi-bg, #e5e7eb)',
    '.dark &': {
      backgroundColor: 'var(--select-multi-bg, #334155)'
    }
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: 'var(--select-text, #111827)',
    '.dark &': {
      color: 'var(--select-text, #e2e8f0)'
    }
  }),
  input: (base) => ({
    ...base,
    margin: '0px',
    color: 'var(--select-text, #111827)',
    '.dark &': {
      color: 'var(--select-text, #e2e8f0)'
    }
  }),
  valueContainer: (base) => ({
    ...base,
    backgroundColor: 'var(--select-bg, #ffffff)',
    '.dark &': {
      backgroundColor: 'var(--select-bg, #1e293b)'
    }
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--select-placeholder, #6b7280)',
    '.dark &': {
      color: 'var(--select-placeholder, #64748b)'
    }
  })
};

export default function FiltersPanel({
  dimensions,
  dimensionCounts = {},
  loadingColumns = {},
  loadingCounts = {},
  filters,
  handleFilterChange,
  onProductChange,
  onMenuOpen,
  onSearch,
  allowedColumns,
  canFilterAi,
  defaultFilters,
  fetchWithAuth,
  handleResetFilters,
  currentAppliedSearch,
  setCurrentAppliedSearch,
  obfuscate,
  jailedFilterOptions = {},
  hiddenColumns = [],
  embedded = false
}) {
  const { t } = useTranslation();
  const filterTranslations = getFilterTranslations(t);
  const visibleColumns = allowedColumns.filter(column => column !== 'createdat' && !hiddenColumns.includes(column));
  // Right under « Item »: a code is searched in the declared item first, and the field says so.
  const productField = onProductChange ? (
    <ProductFilterField
      item={filters.product_item || []}
      ai={filters.product_ai || []}
      isAdmin={canFilterAi}
      onChange={onProductChange}
      styles={SELECT_STYLES}
    />
  ) : null;
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Compare filters excluding the date field
  const isCurrentSearchApplied = currentAppliedSearch && filters && currentAppliedSearch.filters && isEqual(
    Object.fromEntries(
      Object.entries(filters || {}).filter(([key]) => key !== 'createdat')
    ),
    Object.fromEntries(
      Object.entries(currentAppliedSearch.filters || {}).filter(([key]) => key !== 'createdat')
    )
  );

  // Check if filters have been modified from the current applied search
  const hasFilterChanges = currentAppliedSearch && !isCurrentSearchApplied;

  const handleUpdateSavedSearch = async () => {
    try {
      await fetchWithAuth(`${API_BASE_URL}/api/saved-searches/${currentAppliedSearch.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: currentAppliedSearch.name,
          filters: filters
        }),
      });

      // Update the current applied search with new filters
      setCurrentAppliedSearch({
        ...currentAppliedSearch,
        filters: filters
      });
    } catch (error) {
      console.error('Failed to update saved search:', error);
    }
  };

  const handleSaveModalClose = (savedSearch = null) => {
    setShowSaveModal(false);
    if (savedSearch) {
      // Mettre à jour currentAppliedSearch avec la nouvelle recherche sauvegardée
      setCurrentAppliedSearch(savedSearch);
    }
  };

  // Helper function to get dimension count
  const getDimensionCount = (column) => {
    if (loadingCounts[column] && dimensionCounts[column] === undefined)
      return '...';

    return dimensionCounts[column] ?? '—';
  };

  const getDimensionOptions = (column) => {
    if (jailedFilterOptions[column])
      return jailedFilterOptions[column];

    const options = dimensions[column] || [];
    const selectedValues = Array.isArray(filters[column])
      ? filters[column]
      : filters[column] ? [filters[column]] : [];

    if (selectedValues.length === 0)
      return options;

    const mergedOptions = [...options];

    selectedValues.forEach(selectedValue => {
      const hasMatchingOption = mergedOptions.some(option => `${option.value}` === `${selectedValue.value}`);

      if (!hasMatchingOption)
        mergedOptions.push(selectedValue);
    });

    return mergedOptions;
  };

  const getResolvedSelectedValue = (column) => {
    const selectedValue = filters[column];
    const hasJailedOptions = jailedFilterOptions[column] !== undefined;

    if (!selectedValue)
      return null;

    const options = getDimensionOptions(column);
    const resolveValue = (value) => {
      const matchingOption = options.find(option => `${option.value}` === `${value.value}`);
      if (hasJailedOptions)
        return matchingOption || null;

      return matchingOption || value;
    };

    if (Array.isArray(selectedValue))
      return selectedValue.map(resolveValue).filter(Boolean);

    const resolved = resolveValue(selectedValue);
    return resolved ? [resolved] : [];
  };

  const obfuscateOption = (option) => ({
    ...option,
    label: option.label && option.label.length > 2
      ? `${option.label.slice(0, 2)}${'*'.repeat(Math.max(0, option.label.length - 2))}`
      : option.label
  });

  const shouldObfuscateColumn = (column) => obfuscate && OBFUSCATABLE_COLUMNS.includes(column);

  const getSelectValue = (column) => {
    const selectedValue = getResolvedSelectedValue(column);

    if (!shouldObfuscateColumn(column) || !selectedValue)
      return selectedValue;

    return Array.isArray(selectedValue)
      ? selectedValue.map(obfuscateOption)
      : obfuscateOption(selectedValue);
  };

  const getSelectOptions = (column) => {
    const options = getDimensionOptions(column);

    if (!shouldObfuscateColumn(column))
      return options;

    return options.map(obfuscateOption);
  };

  const handleResetCurrentSearch = () => {
    setCurrentAppliedSearch(null);
    handleResetFilters();
  };

  return (
    <div className={`flex flex-col ${embedded ? 'h-full' : 'h-[calc(100vh-4rem)]'}`}>
      {/* Filters Content - scrollable area */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {/* Add current search name display with reset button */}
          {currentAppliedSearch && (
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span>{t('Applied search')}: <span className="font-medium">{currentAppliedSearch.name}</span></span>
                </div>
                <button
                  onClick={handleResetCurrentSearch}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  title={t('Reset filters')}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Update saved search button */}
              {hasFilterChanges && (
                <button
                  onClick={handleUpdateSavedSearch}
                  className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 border border-blue-600 dark:border-blue-400 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {t('Update saved search')}
                </button>
              )}
            </div>
          )}

          {/* {!isDefaultFilters && !isCurrentSearchApplied && !currentAppliedSearch && (
            <div className="flex justify-end">
              <button
                onClick={handleResetFilters}
                className="flex items-center gap-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                title={t('Reset filters')}
              >
                {t('Reset')}
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <div className="border-r border-gray-300 dark:border-gray-600 mx-2 h-6"></div>

              <button
                onClick={() => setShowSaveModal(true)}
                className="flex items-center gap-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                title={t('Save search')}
              >
                {t('Save search')}
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                  />
                </svg>
              </button>
            </div>
          )} */}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('Period')}
            </label>
            <DateFilter
              value={filters.createdat}
              onChange={(dates) => handleFilterChange('createdat', dates)}
            />
          </div>

          {visibleColumns.map((column, index, array) => {
            const isFirstThree = index < 3;
            const isAdminFilter = ['client', 'username'].includes(column);

            return (
              <React.Fragment key={column}>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                  {isAdminFilter && (
                    getAdminBadge()
                  )}
                  {filterTranslations[column] || column.charAt(0).toUpperCase() + column.slice(1)} ({getDimensionCount(column)})
                </label>
                <Select
                  key={`select-${column}-${filters[column]?.value || 'empty'}`}
                  value={getSelectValue(column)}
                  onChange={(selected) => handleFilterChange(column, selected)}
                  onMenuOpen={() => onMenuOpen?.(column)}
                  onFocus={() => onMenuOpen?.(column)}
                  onInputChange={(value, meta) => {
                    if (meta.action === 'input-change') onSearch?.(column, value);
                    return value;
                  }}
                  options={getSelectOptions(column)}
                  isClearable
                  isMulti
                  isLoading={!!loadingColumns?.[column]}
                  menuPlacement={isFirstThree ? 'auto' : 'top'}
                  className="text-sm"
                  classNamePrefix="select"
                  placeholder={t('Select...')}
                  styles={SELECT_STYLES}
                />
              </div>
              {column === 'item' && productField}
              </React.Fragment>
            );
          })}

          {!visibleColumns.includes('item') && productField}

          {canFilterAi && (
            <AiFacetsPanel
              value={filters.ai}
              onChange={(value) => handleFilterChange('ai', value)}
              fetchWithAuth={fetchWithAuth}
            />
          )}
        </div>
      </div>

      {/* Save Search Modal */}
      <SaveSearchModal
        isOpen={showSaveModal}
        onClose={handleSaveModalClose}
        currentFilters={filters}
        fetchWithAuth={fetchWithAuth}
      />
    </div>
  );
}
